import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Aviso, Campo } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";

/**
 * Recuperar la contraseña del equipo de un club, en dos pantallas:
 * pedir el enlace por correo y, con el enlace en la mano, poner la nueva.
 *
 * Se distinguen por si la dirección trae `token`. La misma página sirve para
 * las dos porque el camino es uno solo, y partirlo obligaría a quien llega
 * desde el correo a entender donde esta.
 */
export default function Recuperar() {
  const [parametros] = useSearchParams();
  const token = parametros.get("token");
  return token ? <PonerNueva token={token} /> : <PedirEnlace />;
}

function PedirEnlace() {
  const { t } = useIdioma();
  const [email, setEmail] = useState("");
  const [enviado, setEnviado] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      const r = await api<{ mensaje: string }>("/auth/recuperar", { metodo: "POST", cuerpo: { email } });
      setEnviado(r.mensaje);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("rc.fallo"));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="pantalla-login">
      <form className="caja-login" onSubmit={enviar}>
        <div className="centrado" style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: "1.2rem" }}>{t("rc.titulo")}</h1>
          <p className="suave pequeno" style={{ margin: "4px 0 0" }}>
            {t("rc.subtitulo")}
          </p>
        </div>

        <Aviso tipo="error">{error}</Aviso>

        {enviado ? (
          <>
            <Aviso tipo="ok">{enviado}</Aviso>
            <p className="suave pequeno">{t("rc.miraSpam")}</p>
            <p className="centrado pequeno" style={{ marginBottom: 0 }}>
              <Link to="/">{t("rc.volver")}</Link>
            </p>
          </>
        ) : (
          <>
            <Campo etiqueta={t("rc.email")}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                autoFocus
              />
            </Campo>

            <button className="boton grande bloque" disabled={enviando} style={{ marginTop: 6 }}>
              {enviando ? t("rc.enviando") : t("rc.enviar")}
            </button>

            <p className="centrado pequeno suave" style={{ marginBottom: 0 }}>
              <Link to="/">{t("rc.volver")}</Link>
            </p>
          </>
        )}
      </form>
    </div>
  );
}

function PonerNueva({ token }: { token: string }) {
  const { t } = useIdioma();
  const navegar = useNavigate();
  const [password, setPassword] = useState("");
  const [repetida, setRepetida] = useState("");
  const [error, setError] = useState("");
  const [listo, setListo] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const coinciden = password.length > 0 && password === repetida;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      await api("/auth/restablecer", { metodo: "POST", cuerpo: { token, password } });
      setListo(true);
      setTimeout(() => navegar("/"), 2500);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("rc.falloCambiar"));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="pantalla-login">
      <form className="caja-login" onSubmit={enviar}>
        <div className="centrado" style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: "1.2rem" }}>{t("rc.nueva")}</h1>
        </div>

        <Aviso tipo="error">{error}</Aviso>

        {listo ? (
          <>
            <Aviso tipo="ok">{t("rc.cambiada")}</Aviso>
            <p className="centrado pequeno" style={{ marginBottom: 0 }}>
              <Link to="/">{t("rc.entrarAhora")}</Link>
            </p>
          </>
        ) : (
          <>
            <Campo etiqueta={t("rc.nuevaEtiqueta")} ayuda={t("rc.minimo")}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                autoFocus
              />
            </Campo>
            <Campo etiqueta={t("rc.repitela")}>
              <input
                type="password"
                value={repetida}
                onChange={(e) => setRepetida(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Campo>

            {repetida.length > 0 && !coinciden && <Aviso tipo="error">{t("rc.noCoinciden")}</Aviso>}

            <button
              className="boton grande bloque"
              disabled={enviando || password.length < 8 || !coinciden}
              style={{ marginTop: 6 }}
            >
              {enviando ? t("g.guardando") : t("rc.cambiar")}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
